import React from 'react'
import {
  Box,
  Grid,
  Typography,
  Divider,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SpeedIcon from '@mui/icons-material/Speed'
import BugReportIcon from '@mui/icons-material/BugReport'
import MonitorIcon from '@mui/icons-material/Monitor'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigToggle from '../components/ConfigToggle'
import ConfigSelect from '../components/ConfigSelect'
import { AdvancedConfig } from '../types/EnvironmentConfig.types'
import { environmentConfigSectionDividerSx, environmentConfigSectionRootSx } from '../utils/environmentConfigChrome'

interface AdvancedSettingsProps {
  config: AdvancedConfig
  onChange: (updates: Partial<AdvancedConfig>) => void
  disabled?: boolean
}

/**
 * Advanced Settings Section
 * Manages performance, logging, and monitoring settings
 */
const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  config,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const handleChange = (field: keyof AdvancedConfig, value: any) => {
    onChange({ [field]: value })
  }

  const handleNestedChange = (parentField: keyof AdvancedConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      {/* Performance Settings */}
      <ConfigSection
        title="Performance Settings"
        description="Configure caching, compression, and CDN settings"
        icon={<SpeedIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Caching"
              value={config.performance.caching.enabled}
              onChange={(value) => handleNestedChange('performance', 'caching', { 
                ...config.performance.caching, 
                enabled: value 
              })}
              description="Enable application-level caching for improved performance"
              disabled={disabled}
            />
          </Grid>
          
          {config.performance.caching.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Cache TTL (seconds)"
                  value={config.performance.caching.ttl.toString()}
                  onChange={(value) => handleNestedChange('performance', 'caching', { 
                    ...config.performance.caching, 
                    ttl: parseInt(value) || 3600 
                  })}
                  type="number"
                  placeholder="3600"
                  disabled={disabled}
                  helpText="Time to live for cached items in seconds"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Max Cache Size (MB)"
                  value={config.performance.caching.maxSize.toString()}
                  onChange={(value) => handleNestedChange('performance', 'caching', { 
                    ...config.performance.caching, 
                    maxSize: parseInt(value) || 100 
                  })}
                  type="number"
                  placeholder="100"
                  disabled={disabled}
                  helpText="Maximum cache size in megabytes"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Compression"
              value={config.performance.compression.enabled}
              onChange={(value) => handleNestedChange('performance', 'compression', { 
                ...config.performance.compression, 
                enabled: value 
              })}
              description="Enable gzip compression for static assets and responses"
              disabled={disabled}
            />
          </Grid>
          
          {config.performance.compression.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigSelect
                label="Compression Level"
                value={config.performance.compression.level.toString()}
                onChange={(value) => handleNestedChange('performance', 'compression', { 
                  ...config.performance.compression, 
                  level: parseInt(value) || 6 
                })}
                options={[
                  { value: '1', label: 'Level 1 (Fastest)' },
                  { value: '3', label: 'Level 3' },
                  { value: '6', label: 'Level 6 (Default)' },
                  { value: '9', label: 'Level 9 (Best Compression)' },
                ]}
                disabled={disabled}
                helpText="Compression level from 1 (fastest) to 9 (best compression)"
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable CDN"
              value={config.performance.cdn.enabled}
              onChange={(value) => handleNestedChange('performance', 'cdn', { 
                ...config.performance.cdn, 
                enabled: value 
              })}
              description="Use Content Delivery Network for static assets"
              disabled={disabled}
            />
          </Grid>
          
          {config.performance.cdn.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="CDN Base URL"
                value={config.performance.cdn.url}
                onChange={(value) => handleNestedChange('performance', 'cdn', { 
                  ...config.performance.cdn, 
                  url: value 
                })}
                type="url"
                placeholder="https://cdn.example.com"
                disabled={disabled}
                helpText="CDN base URL for static assets"
              />
            </Grid>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Logging Settings */}
      <ConfigSection
        title="Logging Settings"
        description="Configure application logging and error tracking"
        icon={<BugReportIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Log Level"
              value={config.logging.level}
              onChange={(value) => handleChange('logging', { ...config.logging, level: value })}
              options={[
                { value: 'error', label: 'Error Only' },
                { value: 'warn', label: 'Warnings & Errors' },
                { value: 'info', label: 'Info, Warnings & Errors' },
                { value: 'debug', label: 'Debug (All Levels)' },
              ]}
              disabled={disabled}
              helpText="Minimum log level to record"
            />
          </Grid>
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable File Logging"
              value={config.logging.file.enabled}
              onChange={(value) => handleNestedChange('logging', 'file', { 
                ...config.logging.file, 
                enabled: value 
              })}
              description="Write logs to files on the server"
              disabled={disabled}
            />
          </Grid>
          
          {config.logging.file.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Log File Path"
                  value={config.logging.file.path}
                  onChange={(value) => handleNestedChange('logging', 'file', { 
                    ...config.logging.file, 
                    path: value 
                  })}
                  type="text"
                  placeholder="/var/log/app"
                  disabled={disabled}
                  helpText="Directory path for log files"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Max File Size (MB)"
                  value={config.logging.file.maxSize.toString()}
                  onChange={(value) => handleNestedChange('logging', 'file', { 
                    ...config.logging.file, 
                    maxSize: parseInt(value) || 10 
                  })}
                  type="number"
                  placeholder="10"
                  disabled={disabled}
                  helpText="Maximum size of each log file"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Max Files"
                  value={config.logging.file.maxFiles.toString()}
                  onChange={(value) => handleNestedChange('logging', 'file', { 
                    ...config.logging.file, 
                    maxFiles: parseInt(value) || 5 
                  })}
                  type="number"
                  placeholder="5"
                  disabled={disabled}
                  helpText="Maximum number of log files to keep"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Console Logging"
              value={config.logging.console.enabled}
              onChange={(value) => handleNestedChange('logging', 'console', { 
                ...config.logging.console, 
                enabled: value 
              })}
              description="Output logs to console/terminal"
              disabled={disabled}
            />
          </Grid>
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable External Logging"
              value={config.logging.external.enabled}
              onChange={(value) => handleNestedChange('logging', 'external', { 
                ...config.logging.external, 
                enabled: value 
              })}
              description="Send logs to external logging service"
              disabled={disabled}
            />
          </Grid>
          
          {config.logging.external.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="External Log URL"
                  value={config.logging.external.url}
                  onChange={(value) => handleNestedChange('logging', 'external', { 
                    ...config.logging.external, 
                    url: value 
                  })}
                  type="url"
                  placeholder="https://logs.example.com/api"
                  disabled={disabled}
                  helpText="URL for external logging service"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="API Key"
                  value={config.logging.external.apiKey}
                  onChange={(value) => handleNestedChange('logging', 'external', { 
                    ...config.logging.external, 
                    apiKey: value 
                  })}
                  type="password"
                  placeholder="your-api-key"
                  disabled={disabled}
                  helpText="API key for external logging service"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      {/* Monitoring Settings */}
      <ConfigSection
        title="Monitoring Settings"
        description="Configure health checks, metrics, and alerting"
        icon={<MonitorIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Health Checks"
              value={config.monitoring.healthChecks.enabled}
              onChange={(value) => handleNestedChange('monitoring', 'healthChecks', { 
                ...config.monitoring.healthChecks, 
                enabled: value 
              })}
              description="Enable automatic health check monitoring"
              disabled={disabled}
            />
          </Grid>
          
          {config.monitoring.healthChecks.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Health Check Interval (seconds)"
                value={config.monitoring.healthChecks.interval.toString()}
                onChange={(value) => handleNestedChange('monitoring', 'healthChecks', { 
                  ...config.monitoring.healthChecks, 
                  interval: parseInt(value) || 30 
                })}
                type="number"
                placeholder="30"
                disabled={disabled}
                helpText="Interval between health checks in seconds"
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Metrics Collection"
              value={config.monitoring.metrics.enabled}
              onChange={(value) => handleNestedChange('monitoring', 'metrics', { 
                ...config.monitoring.metrics, 
                enabled: value 
              })}
              description="Collect and expose application metrics"
              disabled={disabled}
            />
          </Grid>
          
          {config.monitoring.metrics.enabled && (
            <Grid item xs={12} md={6}>
              <ConfigField
                label="Metrics Endpoint"
                value={config.monitoring.metrics.endpoint}
                onChange={(value) => handleNestedChange('monitoring', 'metrics', { 
                  ...config.monitoring.metrics, 
                  endpoint: value 
                })}
                type="text"
                placeholder="/metrics"
                disabled={disabled}
                helpText="Endpoint URL for metrics exposure"
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Alerts"
              value={config.monitoring.alerts.enabled}
              onChange={(value) => handleNestedChange('monitoring', 'alerts', { 
                ...config.monitoring.alerts, 
                enabled: value 
              })}
              description="Send alerts for critical issues"
              disabled={disabled}
            />
          </Grid>
          
          {config.monitoring.alerts.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Alert Email"
                  value={config.monitoring.alerts.email.join(', ')}
                  onChange={(value) => handleNestedChange('monitoring', 'alerts', { 
                    ...config.monitoring.alerts, 
                    email: value.split(',').map(e => e.trim()).filter(e => e) 
                  })}
                  type="text"
                  placeholder="admin@example.com, ops@example.com"
                  disabled={disabled}
                  helpText="Comma-separated list of email addresses for alerts"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Webhook URL"
                  value={config.monitoring.alerts.webhook || ''}
                  onChange={(value) => handleNestedChange('monitoring', 'alerts', { 
                    ...config.monitoring.alerts, 
                    webhook: value 
                  })}
                  type="url"
                  placeholder="https://hooks.slack.com/..."
                  disabled={disabled}
                  helpText="Webhook URL for sending alerts (optional)"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>
    </Box>
  )
}

export default AdvancedSettings 